'use client';

import { useContext, type ReactNode } from 'react';
import { RenderOneBlock, ThemeContext } from '@akademi/site-builder-runtime';
import { DelegatingType } from './delegation';
import type { SkinWidth } from './skinnedBlocks';
import { BASE_CSS, TradeStyles } from '../styles/TradeStyles';
import { CATALOGUE_CSS } from '../styles/catalogueCss';
import { PURCHASE_CSS } from '../styles/purchaseCss';
import { PurchaseSteps } from './PurchaseSteps';

/**
 * Styles the platform's functional blocks (listing, basket, checkout, account,
 * forms) to this design system WITHOUT forking them.
 *
 * <h3>How</h3>
 * The theme registers a thin override under the generic block's own type. The
 * override renders a wrapper and then renders the SAME block type again with
 * the theme cleared, so the resolver falls through to the platform's canonical
 * component. All behaviour — basket state, checkout, sign-in, order look-ups —
 * stays in commerce, and this theme owns only the look.
 *
 * <p>Three layers, least invasive first:
 * <ol>
 *   <li><b>Tokens.</b> Most of the look already arrives through the template's
 *       design tokens, which the generic blocks read.</li>
 *   <li><b>Scoped CSS variables.</b> A few names the generic blocks read are not
 *       token paths (`--color-primary`, `--color-on-primary`, `--color-border`,
 *       `--color-text`); setting them on the wrapper makes the blocks' own
 *       `var(...)` lookups resolve to this palette.</li>
 *   <li><b>Scoped rules.</b> Dense ruled tables, the basket summary, and a solid
 *       brand primary button. Where a generic
 *       block sets the same property inline, the rule needs `!important`; those
 *       are the only ones that use it.</li>
 * </ol>
 */
const SKIN_CSS = `
.ae-skin{
  --color-primary:var(--ae-brand);
  --color-on-primary:var(--ae-on-brand);
  --color-text:var(--ae-ink);
  --color-border:var(--ae-border);

  /* The approved geometry (27 September 2026): soft corners and a light lift, read from
     the template's own radius and shadow tokens. Nothing is overridden here any more — the
     generic blocks ask for var(--radius-card) and var(--shadow-md) and get the shop's values.
     The trade-counter look this replaced forced every radius square and every shadow off. */

  padding-block:clamp(20px,2.5vw,36px);
}
/* A route brings its own vertical rhythm (the listing's is py-8). Adding the block
   skin's padding on top of it just pushes the page down. */
.ae-skin[data-ae-skin="route"]{padding-block:0}
.ae-skin--narrow > .ae-skin__inner{max-width:30rem}
.ae-skin--medium > .ae-skin__inner{max-width:60rem}
.ae-skin__inner{margin-inline:auto;padding-inline:var(--ae-gutter);max-width:var(--ae-max)}

.ae-skin h1,.ae-skin h2,.ae-skin h3{font-family:var(--font-family-display,inherit);letter-spacing:-.01em;color:var(--ae-ink)}
.ae-skin h1{font-size:clamp(1.5rem,1.1rem + 1.4vw,2.25rem) !important;font-weight:700}
.ae-skin h2{font-size:clamp(1.15rem,1rem + .6vw,1.5rem);font-weight:700}
.ae-skin h3{font-size:1rem;font-weight:600}

/* Primary action: the brand fill, bold. */
.ae-skin button[type="submit"],.ae-skin form button:not([type="button"]){
  background:var(--ae-brand) !important;color:var(--ae-on-brand) !important;
  border:1px solid var(--ae-brand) !important;border-radius:var(--ae-radius) !important;
  padding:10px 18px !important;font-weight:700 !important;font-size:.9375rem !important;
}
.ae-skin button[type="submit"]:hover,.ae-skin form button:not([type="button"]):hover{
  background:var(--ae-brand-deep) !important;border-color:var(--ae-brand-deep) !important;
}

/* The storefront's own buy buttons (listing card, product page) paint their fill with a
   Tailwind utility, bg-[var(--color-brand-600)]. The platform stylesheet also carries an
   UNLAYERED reset, button{background:none}, and unlayered CSS beats every @layer rule
   whatever its specificity — so on the live shop (27 September 2026, first screenshot) both
   buttons rendered as bare text with no fill. They are matched by that utility class
   because the components carry no semantic class of their own. Amber, as approved: the
   button that spends money is the accent, and amber carries dark ink, not white. */
.ae-skin button[class*="bg-[var(--color-brand-600)]"]{
  background:var(--ae-accent) !important;color:var(--ae-on-accent) !important;
  border-radius:var(--ae-radius);font-weight:700;
}
.ae-skin button[class*="bg-[var(--color-brand-600)]"]:hover:not(:disabled){background:var(--ae-accent-deep) !important;filter:none}

/* Fields: a border you can see, and a focus ring that is not the browser's. */
.ae-skin input:not([type="checkbox"]):not([type="radio"]),.ae-skin select,.ae-skin textarea{
  border:1px solid var(--ae-border-strong) !important;border-radius:var(--ae-radius) !important;
  color:var(--ae-ink);background:var(--ae-surface);padding:9px 11px;
}
.ae-skin input[type="checkbox"],.ae-skin input[type="radio"]{accent-color:var(--ae-brand)}
.ae-skin label{color:var(--ae-ink);font-weight:600;font-size:.875rem}

.ae-skin a{color:var(--ae-link);text-underline-offset:.2em}
.ae-skin a:hover{color:var(--ae-link-hover)}

/* Dense ruled rows — a basket of fourteen lines has to be scannable. */
.ae-skin table{border-collapse:collapse;width:100%}
.ae-skin th,.ae-skin td{border-bottom:1px solid var(--ae-border);padding:10px 8px;text-align:left}
.ae-skin th{font-size:.75rem;text-transform:uppercase;letter-spacing:.04em;color:var(--ae-muted)}

/* The basket summary: a ruled panel on a tint, not a white card floating on white.
   CartView builds its totals as div > dt + dd inside the aside, so the rules land on the
   rows without the block knowing anything about this theme. The last row is the order
   total and is separated by a heavy rule — on a page where every line is a number, the
   one the shopper is about to pay has to be findable without reading the labels. */
.ae-skin .commerce-cart-summary{
  border:1px solid var(--ae-border);
  border-radius:var(--ae-radius-lg);
  background:var(--ae-subtle);
}
.ae-skin .commerce-cart-summary dl > div{
  padding-block:9px;
  border-bottom:1px solid var(--ae-border);
  font-size:.9375rem;
}
.ae-skin .commerce-cart-summary dl > div:last-child{
  margin-top:2px;
  padding-top:11px;
  border-bottom:0;
  border-top:2px solid var(--ae-ink);
  font-size:1.0625rem;
}
/* The one amber button on the page is the one that spends money. Checkout is a LINK, not
   a form button, so the primary-action rule above does not reach it.

   Matched as "the link inside the summary", which is a structural assumption and is
   written down here because the obvious alternative does not work. The block marks that
   slot with useBlockPart, but useBlockPart returns {} unless a BlockIdentity is in
   context — it is there for the site-builder editor's click targets — and the attribute's
   namespace is configurable besides, so data-ak-part is absent on every page a shopper
   ever loads. A rule hung off it would match nothing in production and everything in a
   preview, which is the worst of both.

   The assumption: CartView puts exactly one anchor in .commerce-cart-summary, the
   checkout link. Continue shopping is in the OTHER column, deliberately not a button —
   two buttons of equal weight is how a basket page ends up losing shoppers through the
   loud one. If a delivery note or a coupon error ever adds a link to the aside, it will
   arrive dressed as a 48px amber button and this rule needs narrowing. */
.ae-skin .commerce-cart-summary a[href]{
  display:flex;align-items:center;justify-content:center;
  min-height:48px;
  background:var(--ae-accent);color:var(--ae-on-accent);
  border:1px solid var(--ae-accent);border-radius:var(--ae-radius);
  font-weight:700;text-decoration:none;
}
.ae-skin .commerce-cart-summary a[href]:hover{
  background:var(--ae-accent-deep);border-color:var(--ae-accent-deep);color:var(--ae-on-accent);
}
`;

function SkinFrame({ type, width, cartHref, children }: { type: string; width: SkinWidth; cartHref?: string; children: ReactNode }) {
  return (
    <div className={`ae ae-skin ae-skin--${width}`} data-ae-skin={type}>
      <TradeStyles id="base" css={BASE_CSS} />
      <TradeStyles id="functional-skin" css={SKIN_CSS} />
      <TradeStyles id="purchase" css={PURCHASE_CSS} />
      <div className="ae-skin__inner"><PurchaseSteps type={type} cartHref={cartHref} />{children}</div>
    </div>
  );
}

/**
 * The same surface, for a page this theme does not reach through a block.
 *
 * <p><strong>Why it has to exist.</strong> The skin is registered as a theme override
 * keyed by block type, so it only runs where the site-builder renders a block. The
 * catalogue's three routes — `/c/…`, `/p/…`, `/kits/…` — are concrete Next routes that
 * render this app's own components directly and never enter the block resolver, so the
 * skin never ran on them. The result was a shop whose authored pages were themed and
 * whose listings were platform defaults: same header, and then different type scale,
 * different buttons, different borders the moment you clicked into a category. That
 * reads to a visitor as two different sites, and it is the single most-visited path in
 * the shop.
 *
 * <p>`data-ae-skin="route"` distinguishes it in the DOM from a skinned block, which is
 * worth having when working out which of the two styled something.
 *
 * <p><strong>No `ae-skin__inner`, unlike the block skin.</strong> A block arrives as bare
 * content and needs the frame to centre it and give it a gutter; a route brings its own
 * container — the listing's is `mx-auto max-w-layout px-4 py-8`. Nesting the two centres
 * the content twice and insets it by two gutters, and since `--ae-max` is 1280px against
 * the route's 1200px the narrower one silently wins, so the damage shows up as content
 * that is too narrow rather than as anything obviously broken. The classes and the styles
 * are what this is for; the box is the route's.
 */
export function TradeSurface({ children }: { children: ReactNode }) {
  return (
    <div className="ae ae-skin" data-ae-skin="route">
      <TradeStyles id="base" css={BASE_CSS} />
      <TradeStyles id="functional-skin" css={SKIN_CSS} />
      <TradeStyles id="catalogue" css={CATALOGUE_CSS} />
      {children}
    </div>
  );
}

/** The props the skin needs on top of the block's own, namespaced so they cannot collide. */
export interface TradeSkinProps {
  skinType: string;
  skinWidth: SkinWidth;
  blockProps: Record<string, unknown>;
}

/**
 * The skin itself: one component, told at render time which block it wraps and
 * how wide to sit.
 *
 * <p><strong>It takes the two values as props rather than being a factory.</strong>
 * A `withTradeSkin(type, width)` exported from this `'use client'` module and
 * called while assembling the theme bundle would be called from the server —
 * `../index.ts` builds the bundle at module scope, and the server reaches it
 * through the extensions route — and a function exported across the client
 * boundary is only a reference there. That shape is what made the sibling
 * kitchen storefront unbuildable, so it is not repeated here.
 */
export function TradeSkin({ skinType, skinWidth, blockProps }: TradeSkinProps) {
  const delegating = useContext(DelegatingType);
  // Re-entered for the same type: the cleared theme resolved back to this
  // override, so there is no canonical block to delegate to. Render nothing
  // rather than recurse forever.
  if (delegating === skinType) return null;
  return (
    <SkinFrame type={skinType} width={skinWidth} cartHref={typeof blockProps.cartHref === 'string' ? blockProps.cartHref : undefined}>
      <DelegatingType.Provider value={skinType}>
        <ThemeContext.Provider value={null}>
          <RenderOneBlock instance={{ type: skinType, props: blockProps }} />
        </ThemeContext.Provider>
      </DelegatingType.Provider>
    </SkinFrame>
  );
}
